/**
 * SFXSynthesizer — 8-bit synthesized SFX via Web Audio API.
 * Extracted from SoundManager. Handles all procedural sound generation,
 * MP3 overrides, engine loops, and spatial audio.
 */

import { GOD_SFX, saveGodMode } from '@/config/GodMode';

// ── SFX types ──
export type SfxKey =
  | 'shoot' | 'shootHeavy' | 'missile' | 'burst'
  | 'flame' | 'laser' | 'burnSizzle'
  | 'explosion' | 'missileExplosion'
  | 'hit' | 'enemyDeath' | 'baseHit'
  | 'waveStart' | 'waveComplete'
  | 'robotPlace' | 'robotExplode'
  | 'click' | 'clickConfirm' | 'clickCancel' | 'clickSelect' | 'clickCycle'
  | 'error'
  | 'atkGrunt' | 'atkRunner' | 'atkSwarm' | 'atkTank' | 'atkBrute'
  | 'atkPhantom' | 'atkDrone' | 'atkWasp' | 'atkBomber' | 'atkSpecter'
  | 'enemyShootTank' | 'enemyShootDrone' | 'enemyShootBomber'
  | 'enemyShootBrute' | 'enemyShootSpecter'
  | 'streak' | 'waveClear'
  | 'repair' | 'coin' | 'componentBreak'
  | 'evolve' | 'mineLay'
  // ── New: missing game events ──
  | 'victory' | 'defeat'
  | 'baseDestroyed'
  | 'enemySpawn'
  | 'robotDeath'
  | 'phaseChange'
  | 'levelUnlock' | 'componentUnlock' | 'gridExpand'
  | 'platinumReveal' | 'medalReveal'
  | 'steamVent'
  | 'stamp'
  | 'combo'
  // ── Ambient life sounds ──
  | 'jumpBoing' | 'jumpCompress'
  | 'radarPing' | 'servoTurn' | 'enemyGrowl' | 'enemyDeathGrunt' | 'robotClank'
  | 'runnerGrowl'
  // ── Terrain death SFX ──
  | 'mining' | 'harvestComplete'
  | 'terrainDrown' | 'terrainBurn' | 'terrainFall'
  // ── Engine loops (volume-only keys — continuous oscillators, not one-shot) ──
  | 'engineRobotWheel' | 'engineRobotFly' | 'engineEnemyGround' | 'engineEnemyFly';

// ── Engine loop voice definition ──
type EngineVoiceKey = 'robotWheel' | 'robotFly' | 'enemyGround' | 'enemyFly';

interface EngineVoice {
  osc: OscillatorNode;
  filter?: BiquadFilterNode;
  gain: GainNode;
  running: boolean;
}

/** Attenuation max distance: 10 tile diagonals */
const ENGINE_MAX_DIST = 10 * 40 * Math.SQRT2; // 40 = TILE_SIZE

/** Non-organic (mechanical) enemy types that produce engine sounds. */
export const MECHANICAL_ENEMY_TYPES: ReadonlySet<string> = new Set([
  'tank', 'drone', 'bomber', 'wasp', 'specter',
]);

/**
 * Entity position info passed to updateEngineLoops().
 * Minimal interface so GameScene doesn't need to import heavy entity types.
 */
export interface EngineSoundEntity {
  x: number;
  y: number;
  alive: boolean;
  voice: EngineVoiceKey;
}

/**
 * Handles all SFX: procedural synthesis, MP3 overrides, engine loops, spatial audio.
 * Instantiated internally by SoundManager.
 */
export class SFXSynthesizer {
  private ctx: AudioContext | null = null;

  /** MP3 override system */
  private sfxOverrides: Map<SfxKey, HTMLAudioElement> = new Map();
  private sfxOverrideChecked: Set<SfxKey> = new Set();

  // ── Engine loop system ──
  private engineVoices: Map<EngineVoiceKey, EngineVoice> = new Map();
  private engineMasterGain: GainNode | null = null;
  private engineLoopsActive = false;

  /** Volume/mute getters owned by SoundManager */
  private getFxVolume: () => number;
  private isMuted: () => boolean;

  constructor(getFxVolume: () => number, isMuted: () => boolean) {
    this.getFxVolume = getFxVolume;
    this.isMuted = isMuted;
  }

  // ── AudioContext management ──

  /** Ensure AudioContext exists (must be called from user gesture) */
  ensureContext(): AudioContext {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
    return this.ctx;
  }

  getContext(): AudioContext | null {
    return this.ctx;
  }

  // ── MP3 Override Loading ──

  loadSfxOverrides(): void {
    const allKeys: SfxKey[] = [
      'shoot', 'shootHeavy', 'missile', 'burst', 'flame', 'laser',
      'explosion', 'missileExplosion', 'hit', 'enemyDeath', 'baseHit',
      'waveStart', 'waveComplete', 'robotPlace', 'robotExplode',
      'click', 'clickConfirm', 'clickCancel', 'clickSelect', 'clickCycle',
      'error',
      'atkGrunt', 'atkRunner', 'atkSwarm', 'atkTank', 'atkBrute',
      'atkPhantom', 'atkDrone', 'atkWasp', 'atkBomber', 'atkSpecter',
      'enemyShootTank', 'enemyShootDrone', 'enemyShootBomber',
      'enemyShootBrute', 'enemyShootSpecter',
      'streak', 'waveClear', 'repair', 'coin', 'componentBreak', 'evolve',
      'victory', 'defeat', 'baseDestroyed', 'enemySpawn', 'robotDeath',
      'phaseChange', 'levelUnlock', 'componentUnlock', 'gridExpand', 'platinumReveal', 'medalReveal', 'steamVent',
      'jumpBoing', 'jumpCompress', 'combo',
      'radarPing', 'servoTurn', 'enemyGrowl', 'enemyDeathGrunt', 'robotClank',
      'burnSizzle',
      'mining', 'harvestComplete',
    ];
    for (const key of allKeys) {
      const audio = new Audio(`/sounds/sfx/${key}.mp3`);
      audio.preload = 'auto';
      audio.addEventListener('canplaythrough', () => {
        this.sfxOverrides.set(key, audio);
      }, { once: true });
      audio.addEventListener('error', () => {
        this.sfxOverrideChecked.add(key);
      }, { once: true });
    }
  }

  /** Try to play an MP3 override. Returns true if override was played. */
  private playSfxOverride(key: SfxKey): boolean {
    const audio = this.sfxOverrides.get(key);
    if (!audio) return false;
    const clone = audio.cloneNode() as HTMLAudioElement;
    const perKeyVol = GOD_SFX[key] ?? 1;
    clone.volume = Math.min(1, this.getFxVolume() * perKeyVol);
    clone.play().catch(() => {});
    return true;
  }

  // ── Per-SFX volume API ──

  getSfxVolume(key: SfxKey): number {
    return GOD_SFX[key] ?? 1;
  }

  setSfxVolume(key: SfxKey, v: number): void {
    GOD_SFX[key] = Math.max(0, Math.min(5, v));
    saveGodMode();
  }

  exportSfxVolumes(): Record<string, number> {
    return { ...GOD_SFX };
  }

  importSfxVolumes(data: Record<string, number>): void {
    for (const [k, v] of Object.entries(data)) {
      if (typeof v === 'number') GOD_SFX[k] = Math.max(0, Math.min(5, v));
    }
    saveGodMode();
  }

  // ── Engine loop master gain sync ──

  /** Update engine master gain when fxVolume or muted changes */
  syncEngineGain(): void {
    if (this.engineMasterGain && this.ctx) {
      this.engineMasterGain.gain.setValueAtTime(
        this.isMuted() ? 0 : this.getFxVolume() * 0.25, this.ctx.currentTime,
      );
    }
  }

  // ── SFX playback dispatcher ──

  play(key: SfxKey): void {
    if (this.isMuted() || this.getFxVolume() <= 0) return;
    if (this.playSfxOverride(key)) return;
    try {
      const ctx = this.ensureContext();
      const perKeyVol = GOD_SFX[key] ?? 1;
      const vol = this.getFxVolume() * perKeyVol;
      switch (key) {
        case 'shoot':       this.synthShoot(ctx, vol); break;
        case 'shootHeavy':  this.synthShootHeavy(ctx, vol); break;
        case 'missile':     this.synthMissile(ctx, vol); break;
        case 'burst':       this.synthBurst(ctx, vol); break;
        case 'flame':       this.synthFlame(ctx, vol); break;
        case 'laser':       this.synthLaser(ctx, vol); break;
        case 'explosion':   this.synthExplosion(ctx, vol, 0.7); break;
        case 'missileExplosion': this.synthExplosion(ctx, vol, 1.2); break;
        case 'hit':         this.synthHit(ctx, vol); break;
        case 'enemyDeath':  this.synthEnemyDeath(ctx, vol); break;
        case 'baseHit':     this.synthBaseHit(ctx, vol); break;
        case 'waveStart':   this.synthWaveStart(ctx, vol); break;
        case 'waveComplete': this.synthWaveComplete(ctx, vol); break;
        case 'robotPlace':  this.synthPlace(ctx, vol); break;
        case 'robotExplode': this.synthExplosion(ctx, vol, 1.4); break;
        case 'click':       this.synthClick(ctx, vol); break;
        case 'clickConfirm': this.synthClickConfirm(ctx, vol); break;
        case 'clickCancel': this.synthClickCancel(ctx, vol); break;
        case 'clickSelect': this.synthClickSelect(ctx, vol); break;
        case 'clickCycle':  this.synthClickCycle(ctx, vol); break;
        case 'error':       this.synthError(ctx, vol); break;
        case 'atkGrunt':    this.synthAtkGrunt(ctx, vol); break;
        case 'atkRunner':   this.synthAtkRunner(ctx, vol); break;
        case 'atkSwarm':    this.synthAtkSwarm(ctx, vol); break;
        case 'atkTank':     this.synthAtkTank(ctx, vol); break;
        case 'atkBrute':    this.synthAtkBrute(ctx, vol); break;
        case 'atkPhantom':  this.synthAtkPhantom(ctx, vol); break;
        case 'atkDrone':    this.synthAtkDrone(ctx, vol); break;
        case 'atkWasp':     this.synthAtkWasp(ctx, vol); break;
        case 'atkBomber':   this.synthAtkBomber(ctx, vol); break;
        case 'atkSpecter':  this.synthAtkSpecter(ctx, vol); break;
        case 'enemyShootTank':   this.synthEnemyShootHeavy(ctx, vol, 220); break;
        case 'enemyShootDrone':  this.synthEnemyShootZap(ctx, vol, 1600); break;
        case 'enemyShootBomber': this.synthEnemyShootBomber(ctx, vol); break;
        case 'enemyShootBrute':  this.synthEnemyShootHeavy(ctx, vol, 100); break;
        case 'enemyShootSpecter':this.synthEnemyShootZap(ctx, vol, 800); break;
        case 'streak':    this.synthStreak(ctx, vol); break;
        case 'waveClear':  this.synthWaveClear(ctx, vol); break;
        case 'repair':    this.synthRepair(ctx, vol); break;
        case 'coin':      this.synthCoin(ctx, vol); break;
        case 'componentBreak': this.synthComponentBreak(ctx, vol); break;
        case 'evolve':   this.synthEvolve(ctx, vol); break;
        case 'mineLay':  this.synthMineLay(ctx, vol); break;
        case 'victory':        this.synthVictory(ctx, vol); break;
        case 'defeat':         this.synthDefeat(ctx, vol); break;
        case 'baseDestroyed':  this.synthBaseDestroyed(ctx, vol); break;
        case 'enemySpawn':     this.synthEnemySpawn(ctx, vol); break;
        case 'robotDeath':     this.synthRobotDeath(ctx, vol); break;
        case 'phaseChange':    this.synthPhaseChange(ctx, vol); break;
        case 'levelUnlock':    this.synthLevelUnlock(ctx, vol); break;
        case 'componentUnlock': this.synthComponentUnlock(ctx, vol); break;
        case 'gridExpand':     this.synthGridExpand(ctx, vol); break;
        case 'platinumReveal': this.synthPlatinumReveal(ctx, vol); break;
        case 'medalReveal': this.synthMedalReveal(ctx, vol); break;
        case 'steamVent': this.synthSteamVent(ctx, vol); break;
        case 'jumpBoing': this.synthJumpBoing(ctx, vol); break;
        case 'jumpCompress': this.synthJumpCompress(ctx, vol); break;
        case 'combo': this.synthCombo(ctx, vol); break;
        case 'stamp':          this.synthStamp(ctx, vol); break;
        case 'radarPing':       this.synthRadarPing(ctx, vol); break;
        case 'servoTurn':       this.synthServoTurn(ctx, vol); break;
        case 'enemyGrowl':      this.synthEnemyGrowl(ctx, vol); break;
        case 'enemyDeathGrunt': this.synthEnemyDeathGrunt(ctx, vol); break;
        case 'robotClank':      this.synthRobotClank(ctx, vol); break;
        case 'runnerGrowl':     this.synthRunnerGrowl(ctx, vol); break;
        case 'burnSizzle':      this.synthBurnSizzle(ctx, vol); break;
        case 'mining':          this.synthMining(ctx, vol); break;
        case 'harvestComplete': this.synthHarvestComplete(ctx, vol); break;
        case 'terrainDrown':    this.synthTerrainDrown(ctx, vol); break;
        case 'terrainBurn':     this.synthTerrainBurn(ctx, vol); break;
        case 'terrainFall':     this.synthTerrainFall(ctx, vol); break;
        case 'engineRobotWheel':  this.synthEnginePreview(ctx, vol, 'sawtooth', 45, 200); break;
        case 'engineRobotFly':    this.synthEnginePreview(ctx, vol, 'triangle', 140, 400); break;
        case 'engineEnemyGround': this.synthEnginePreview(ctx, vol, 'square', 55, 250); break;
        case 'engineEnemyFly':    this.synthEnginePreview(ctx, vol, 'sawtooth', 110, 350); break;
      }
    } catch (e) { console.warn('[SoundManager] preview failed:', e); }
  }

  /**
   * Play a one-shot SFX with spatial attenuation from camera.
   */
  playAt(key: SfxKey, worldX: number, worldY: number, camX: number, camY: number): void {
    if (this.isMuted() || this.getFxVolume() <= 0) return;
    const dist = Math.abs(worldX - camX) + Math.abs(worldY - camY);
    if (dist >= ENGINE_MAX_DIST) return;
    const ratio = 1 - dist / ENGINE_MAX_DIST;
    // Temporarily adjust volume for spatial attenuation
    const origGetFx = this.getFxVolume;
    const savedVol = this.getFxVolume();
    this.getFxVolume = () => savedVol * ratio * ratio;
    this.play(key);
    this.getFxVolume = origGetFx;
  }

  // ══════════════════════════════════════════
  //  ENGINE LOOP SYSTEM — spatial motor sounds
  // ══════════════════════════════════════════

  startEngineLoops(): void {
    if (this.engineLoopsActive) return;
    try {
      const ctx = this.ensureContext();
      this.engineMasterGain = ctx.createGain();
      this.engineMasterGain.gain.setValueAtTime(
        this.isMuted() ? 0 : this.getFxVolume() * 0.25, ctx.currentTime,
      );
      this.engineMasterGain.connect(ctx.destination);

      this.createEngineVoice(ctx, 'robotWheel', 'sawtooth', 45, 200);
      this.createEngineVoice(ctx, 'robotFly', 'triangle', 140, 400);
      this.createEngineVoice(ctx, 'enemyGround', 'square', 55, 250);
      this.createEngineVoice(ctx, 'enemyFly', 'sawtooth', 110, 350);

      this.engineLoopsActive = true;
    } catch (e) { console.warn('[SoundManager] startEngineLoops failed:', e); }
  }

  private createEngineVoice(
    ctx: AudioContext, key: EngineVoiceKey,
    waveType: OscillatorType, freq: number, filterFreq: number,
  ): void {
    const osc = ctx.createOscillator();
    osc.type = waveType;
    osc.frequency.setValueAtTime(freq, ctx.currentTime);

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(filterFreq, ctx.currentTime);
    filter.Q.setValueAtTime(1.5, ctx.currentTime);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, ctx.currentTime);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.engineMasterGain!);
    osc.start();

    this.engineVoices.set(key, { osc, filter, gain, running: true });
  }

  stopEngineLoops(): void {
    if (!this.engineLoopsActive) return;
    for (const [, voice] of this.engineVoices) {
      try { voice.osc.stop(); } catch (e) { console.warn('[SoundManager] osc.stop failed:', e); }
      voice.running = false;
    }
    this.engineVoices.clear();
    if (this.engineMasterGain) {
      this.engineMasterGain.disconnect();
      this.engineMasterGain = null;
    }
    this.engineLoopsActive = false;
  }

  updateEngineLoops(entities: EngineSoundEntity[], camX: number, camY: number): void {
    if (!this.engineLoopsActive || this.isMuted()) return;

    const nearest: Record<EngineVoiceKey, number> = {
      robotWheel: Infinity,
      robotFly: Infinity,
      enemyGround: Infinity,
      enemyFly: Infinity,
    };

    for (let i = 0; i < entities.length; i++) {
      const e = entities[i];
      if (!e.alive) continue;
      const dx = e.x - camX;
      const dy = e.y - camY;
      const dist = Math.abs(dx) + Math.abs(dy);
      if (dist < nearest[e.voice]) nearest[e.voice] = dist;
    }

    const voiceToSfx: Record<EngineVoiceKey, SfxKey> = {
      robotWheel: 'engineRobotWheel',
      robotFly: 'engineRobotFly',
      enemyGround: 'engineEnemyGround',
      enemyFly: 'engineEnemyFly',
    };

    const t = this.ctx!.currentTime;
    for (const [key, voice] of this.engineVoices) {
      const vk = key as EngineVoiceKey;
      const dist = nearest[vk];
      const perKeyVol = GOD_SFX[voiceToSfx[vk]] ?? 1;
      let vol: number;
      if (dist >= ENGINE_MAX_DIST || perKeyVol <= 0) {
        vol = 0;
      } else {
        const ratio = 1 - dist / ENGINE_MAX_DIST;
        vol = ratio * ratio * perKeyVol;
      }
      voice.gain.gain.linearRampToValueAtTime(vol, t + 0.05);
    }
  }

  /** Short 0.5s burst preview of an engine voice (for SFX TEST play button) */
  private synthEnginePreview(
    ctx: AudioContext, vol: number,
    waveType: OscillatorType, freq: number, filterFreq: number,
  ): void {
    const t = ctx.currentTime;
    const dur = 0.5;
    const osc = ctx.createOscillator();
    osc.type = waveType;
    osc.frequency.setValueAtTime(freq, t);
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(filterFreq, t);
    filter.Q.setValueAtTime(1.5, t);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(vol * 0.3, t + 0.05);
    g.gain.setValueAtTime(vol * 0.3, t + dur - 0.1);
    g.gain.linearRampToValueAtTime(0, t + dur);
    g.connect(ctx.destination);
    osc.connect(filter);
    filter.connect(g);
    osc.start(t);
    osc.stop(t + dur);
  }

  // ══════════════════════════════════════════════
  // ── 8-bit SFX synthesis (Web Audio API) ──
  // ══════════════════════════════════════════════

  private gain(ctx: AudioContext, vol: number, dur: number): GainNode {
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol * 0.3, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
    g.connect(ctx.destination);
    return g;
  }

  /** Heuristic variation: returns value +/- spread% (default +/-8%) */
  private vary(base: number, spread = 0.08): number {
    return base * (1 + (Math.random() * 2 - 1) * spread);
  }

  private synthShoot(ctx: AudioContext, vol: number): void {
    const osc = ctx.createOscillator();
    const dur = this.vary(0.08);
    const g = this.gain(ctx, vol, dur);
    osc.type = 'square';
    osc.frequency.setValueAtTime(this.vary(880), ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(this.vary(220), ctx.currentTime + dur);
    osc.connect(g);
    osc.start(); osc.stop(ctx.currentTime + dur);
  }

  private synthShootHeavy(ctx: AudioContext, vol: number): void {
    const osc = ctx.createOscillator();
    const dur = this.vary(0.15);
    const g = this.gain(ctx, vol, dur);
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(this.vary(440), ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(this.vary(55), ctx.currentTime + dur);
    osc.connect(g);
    osc.start(); osc.stop(ctx.currentTime + dur);
  }

  private synthMissile(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const dur = this.vary(0.45, 0.03);
    const sr = ctx.sampleRate;
    const popLen = Math.floor(sr * 0.06);
    const popBuf = ctx.createBuffer(1, popLen, sr);
    const pd = popBuf.getChannelData(0);
    for (let i = 0; i < popLen; i++) pd[i] = Math.random() * 2 - 1;
    const popSrc = ctx.createBufferSource();
    popSrc.buffer = popBuf;
    const popHp = ctx.createBiquadFilter();
    popHp.type = 'highpass';
    popHp.frequency.value = this.vary(1800);
    const popGain = ctx.createGain();
    popGain.gain.setValueAtTime(vol * 0.6, t);
    popGain.gain.exponentialRampToValueAtTime(0.001, t + 0.06);
    popSrc.connect(popHp).connect(popGain).connect(ctx.destination);
    popSrc.start(t); popSrc.stop(t + 0.06);
    const jetLen = Math.floor(sr * dur);
    const jetBuf = ctx.createBuffer(1, jetLen, sr);
    const jd = jetBuf.getChannelData(0);
    for (let i = 0; i < jetLen; i++) jd[i] = Math.random() * 2 - 1;
    const jetSrc = ctx.createBufferSource();
    jetSrc.buffer = jetBuf;
    const jetBp = ctx.createBiquadFilter();
    jetBp.type = 'bandpass';
    jetBp.Q.value = this.vary(1.8);
    jetBp.frequency.setValueAtTime(this.vary(400), t);
    jetBp.frequency.exponentialRampToValueAtTime(this.vary(2200), t + dur * 0.7);
    jetBp.frequency.exponentialRampToValueAtTime(this.vary(1400), t + dur);
    const jetGain = ctx.createGain();
    jetGain.gain.setValueAtTime(0, t);
    jetGain.gain.linearRampToValueAtTime(vol * 0.4, t + 0.03);
    jetGain.gain.setValueAtTime(vol * 0.4, t + dur * 0.5);
    jetGain.gain.exponentialRampToValueAtTime(0.001, t + dur);
    jetSrc.connect(jetBp).connect(jetGain).connect(ctx.destination);
    jetSrc.start(t); jetSrc.stop(t + dur);
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(this.vary(180), t);
    osc.frequency.exponentialRampToValueAtTime(this.vary(600), t + dur * 0.6);
    osc.frequency.exponentialRampToValueAtTime(this.vary(350), t + dur);
    const oscGain = ctx.createGain();
    oscGain.gain.setValueAtTime(0, t);
    oscGain.gain.linearRampToValueAtTime(vol * 0.2, t + 0.04);
    oscGain.gain.setValueAtTime(vol * 0.2, t + dur * 0.4);
    oscGain.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.connect(oscGain).connect(ctx.destination);
    osc.start(t); osc.stop(t + dur);
  }

  private synthBurst(ctx: AudioContext, vol: number): void {
    const baseFreq = this.vary(660);
    for (let i = 0; i < 3; i++) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      g.gain.setValueAtTime(vol * 0.2, ctx.currentTime + i * 0.04);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.04 + 0.03);
      g.connect(ctx.destination);
      osc.type = 'square';
      osc.frequency.setValueAtTime(baseFreq + i * this.vary(110), ctx.currentTime + i * 0.04);
      osc.connect(g);
      osc.start(ctx.currentTime + i * 0.04);
      osc.stop(ctx.currentTime + i * 0.04 + 0.03);
    }
  }

  private synthFlame(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const dur = 0.25;
    const sr = ctx.sampleRate;
    const bufSize = Math.floor(sr * dur);
    const buf = ctx.createBuffer(1, bufSize, sr);
    const d = buf.getChannelData(0);
    const grainSize = Math.floor(sr * 0.002);
    let sample = 0;
    for (let i = 0; i < bufSize; i++) {
      if (i % grainSize === 0) sample = Math.random() * 2 - 1;
      d[i] = sample * 0.6 + (Math.random() * 2 - 1) * 0.4;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(this.vary(800), t);
    lp.frequency.linearRampToValueAtTime(this.vary(500), t + dur);
    lp.Q.setValueAtTime(1, t);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(this.vary(250), t);
    bp.Q.setValueAtTime(2, t);
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol * 0.3, t);
    g.gain.setValueAtTime(vol * 0.3, t + dur - 0.01);
    g.gain.linearRampToValueAtTime(0, t + dur);
    g.connect(ctx.destination);
    src.connect(lp); lp.connect(bp); bp.connect(g);
    src.start(t); src.stop(t + dur);
  }

  private synthLaser(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const dur = 0.2;
    const hiOsc = ctx.createOscillator();
    hiOsc.type = 'sawtooth';
    hiOsc.frequency.setValueAtTime(this.vary(3200), t);
    hiOsc.frequency.exponentialRampToValueAtTime(this.vary(1200), t + dur);
    const hiG = ctx.createGain();
    hiG.gain.setValueAtTime(vol * 0.2, t);
    hiG.gain.setValueAtTime(vol * 0.2, t + dur * 0.6);
    hiG.gain.exponentialRampToValueAtTime(0.001, t + dur);
    hiG.connect(ctx.destination);
    hiOsc.connect(hiG);
    hiOsc.start(t); hiOsc.stop(t + dur);
    const loOsc = ctx.createOscillator();
    loOsc.type = 'sine';
    loOsc.frequency.setValueAtTime(this.vary(120), t);
    loOsc.frequency.exponentialRampToValueAtTime(this.vary(50), t + dur * 0.8);
    const loG = ctx.createGain();
    loG.gain.setValueAtTime(vol * 0.35, t);
    loG.gain.exponentialRampToValueAtTime(0.001, t + dur * 0.8);
    loG.connect(ctx.destination);
    loOsc.connect(loG);
    loOsc.start(t); loOsc.stop(t + dur);
    const midOsc = ctx.createOscillator();
    midOsc.type = 'square';
    midOsc.frequency.setValueAtTime(this.vary(800), t);
    midOsc.frequency.exponentialRampToValueAtTime(this.vary(300), t + dur);
    const midG = ctx.createGain();
    midG.gain.setValueAtTime(vol * 0.12, t);
    midG.gain.exponentialRampToValueAtTime(0.001, t + dur);
    midG.connect(ctx.destination);
    midOsc.connect(midG);
    midOsc.start(t); midOsc.stop(t + dur);
    const nLen = Math.floor(ctx.sampleRate * 0.08);
    const nBuf = ctx.createBuffer(1, nLen, ctx.sampleRate);
    const nd = nBuf.getChannelData(0);
    for (let i = 0; i < nLen; i++) nd[i] = (Math.random() * 2 - 1) * (1 - i / nLen);
    const nSrc = ctx.createBufferSource();
    nSrc.buffer = nBuf;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.setValueAtTime(4000, t);
    const nG = ctx.createGain();
    nG.gain.setValueAtTime(vol * 0.1, t);
    nG.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
    nG.connect(ctx.destination);
    nSrc.connect(hp); hp.connect(nG);
    nSrc.start(t); nSrc.stop(t + 0.08);
  }

  private synthExplosion(ctx: AudioContext, vol: number, duration: number): void {
    const t = ctx.currentTime;
    const dur = this.vary(duration);
    const dest = ctx.destination;

    // ── Layer 1: Initial PUNCH (short pitched-down sine burst) ──
    const punch = ctx.createOscillator();
    punch.type = 'sine';
    punch.frequency.setValueAtTime(this.vary(160), t);
    punch.frequency.exponentialRampToValueAtTime(this.vary(30), t + 0.15);
    const punchG = ctx.createGain();
    punchG.gain.setValueAtTime(vol * 0.7, t);
    punchG.gain.setValueAtTime(vol * 0.7, t + 0.01);
    punchG.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
    // Waveshaper for distortion on the punch
    const distortion = ctx.createWaveShaper();
    const curve = new Float32Array(256);
    for (let i = 0; i < 256; i++) {
      const x = (i / 128) - 1;
      curve[i] = (Math.PI + 3.5) * x / (Math.PI + 3.5 * Math.abs(x));
    }
    distortion.curve = curve;
    punch.connect(distortion);
    distortion.connect(punchG);
    punchG.connect(dest);
    punch.start(t);
    punch.stop(t + 0.25);

    // ── Layer 2: Sub BOOM (deep sine) ──
    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(this.vary(50), t);
    sub.frequency.exponentialRampToValueAtTime(this.vary(15), t + dur * 0.5);
    const subG = ctx.createGain();
    subG.gain.setValueAtTime(vol * 0.5, t + 0.02);
    subG.gain.exponentialRampToValueAtTime(0.001, t + dur * 0.5);
    sub.connect(subG);
    subG.connect(dest);
    sub.start(t);
    sub.stop(t + dur * 0.5);

    // ── Layer 3: Noise body (filtered white noise with resonance) ──
    const noiseLen = Math.floor(ctx.sampleRate * dur);
    const noiseBuf = ctx.createBuffer(1, noiseLen, ctx.sampleRate);
    const nd = noiseBuf.getChannelData(0);
    for (let i = 0; i < noiseLen; i++) {
      nd[i] = (Math.random() * 2 - 1);
    }
    const noiseSrc = ctx.createBufferSource();
    noiseSrc.buffer = noiseBuf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(this.vary(800), t);
    bp.frequency.exponentialRampToValueAtTime(this.vary(120), t + dur * 0.7);
    bp.Q.setValueAtTime(1.5, t);
    const noiseG = ctx.createGain();
    noiseG.gain.setValueAtTime(0.001, t);
    noiseG.gain.linearRampToValueAtTime(vol * 0.35, t + 0.03);
    noiseG.gain.exponentialRampToValueAtTime(0.001, t + dur);
    noiseSrc.connect(bp);
    bp.connect(noiseG);
    noiseG.connect(dest);
    noiseSrc.start(t);
    noiseSrc.stop(t + dur);

    // ── Layer 4: High crackle (sparse impulses through highpass) ──
    const crackDur = dur * 0.4;
    const crackLen = Math.floor(ctx.sampleRate * crackDur);
    const crackBuf = ctx.createBuffer(1, crackLen, ctx.sampleRate);
    const cd = crackBuf.getChannelData(0);
    for (let i = 0; i < crackLen; i++) {
      cd[i] = Math.random() > 0.85 ? (Math.random() * 2 - 1) * (1 - i / crackLen) : 0;
    }
    const crackSrc = ctx.createBufferSource();
    crackSrc.buffer = crackBuf;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.setValueAtTime(this.vary(3000), t);
    hp.frequency.exponentialRampToValueAtTime(this.vary(1500), t + crackDur);
    const crackG = ctx.createGain();
    crackG.gain.setValueAtTime(vol * 0.2, t + 0.01);
    crackG.gain.exponentialRampToValueAtTime(0.001, t + crackDur);
    crackSrc.connect(hp);
    hp.connect(crackG);
    crackG.connect(dest);
    crackSrc.start(t + 0.01);
    crackSrc.stop(t + crackDur);

    // ── Layer 5: Tail rumble (very low noise fade) ──
    if (dur > 0.8) {
      const tailLen = Math.floor(ctx.sampleRate * dur * 0.6);
      const tailBuf = ctx.createBuffer(1, tailLen, ctx.sampleRate);
      const td = tailBuf.getChannelData(0);
      for (let i = 0; i < tailLen; i++) {
        td[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / tailLen, 2);
      }
      const tailSrc = ctx.createBufferSource();
      tailSrc.buffer = tailBuf;
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.setValueAtTime(this.vary(200), t + dur * 0.3);
      lp.frequency.exponentialRampToValueAtTime(40, t + dur);
      const tailG = ctx.createGain();
      tailG.gain.setValueAtTime(0.001, t + dur * 0.15);
      tailG.gain.linearRampToValueAtTime(vol * 0.15, t + dur * 0.25);
      tailG.gain.exponentialRampToValueAtTime(0.001, t + dur);
      tailSrc.connect(lp);
      lp.connect(tailG);
      tailG.connect(dest);
      tailSrc.start(t + dur * 0.15);
      tailSrc.stop(t + dur);
    }
  }

  private synthHit(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const dur = this.vary(0.06);
    const osc = ctx.createOscillator();
    const g = this.gain(ctx, vol * 0.4, dur);
    osc.type = 'square';
    osc.frequency.setValueAtTime(this.vary(600), t);
    osc.frequency.exponentialRampToValueAtTime(this.vary(150), t + dur);
    osc.connect(g);
    osc.start(t); osc.stop(t + dur);
    const nLen = Math.floor(ctx.sampleRate * this.vary(0.05));
    const nBuf = ctx.createBuffer(1, nLen, ctx.sampleRate);
    const nd = nBuf.getChannelData(0);
    for (let i = 0; i < nLen; i++) nd[i] = (Math.random() * 2 - 1) * (1 - i / nLen);
    const nSrc = ctx.createBufferSource();
    nSrc.buffer = nBuf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(this.vary(1200), t);
    bp.Q.setValueAtTime(1.5, t);
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(vol * 0.35, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
    ng.connect(ctx.destination);
    nSrc.connect(bp); bp.connect(ng);
    nSrc.start(t); nSrc.stop(t + 0.05);
  }

  private synthEnemyDeath(ctx: AudioContext, vol: number): void {
    const osc = ctx.createOscillator();
    const dur = this.vary(0.18);
    const g = this.gain(ctx, vol * 0.4, dur);
    osc.type = 'square';
    osc.frequency.setValueAtTime(this.vary(500), ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(this.vary(80), ctx.currentTime + dur);
    osc.connect(g);
    osc.start(); osc.stop(ctx.currentTime + dur);
  }

  private synthBaseHit(ctx: AudioContext, vol: number): void {
    const osc = ctx.createOscillator();
    const g = this.gain(ctx, vol * 0.6, 0.2);
    osc.type = 'sawtooth';
    const lo = this.vary(150), hi = this.vary(200);
    osc.frequency.setValueAtTime(lo, ctx.currentTime);
    osc.frequency.setValueAtTime(hi, ctx.currentTime + 0.05);
    osc.frequency.setValueAtTime(lo, ctx.currentTime + 0.1);
    osc.frequency.setValueAtTime(hi, ctx.currentTime + 0.15);
    osc.connect(g);
    osc.start(); osc.stop(ctx.currentTime + 0.2);
  }

  private synthWaveStart(ctx: AudioContext, vol: number): void {
    const baseF = this.vary(440);
    for (let i = 0; i < 3; i++) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      const t = ctx.currentTime + i * 0.12;
      g.gain.setValueAtTime(vol * 0.25, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
      g.connect(ctx.destination);
      osc.type = 'square';
      osc.frequency.setValueAtTime(baseF + i * this.vary(220), t);
      osc.connect(g);
      osc.start(t); osc.stop(t + 0.1);
    }
  }

  private synthWaveComplete(ctx: AudioContext, vol: number): void {
    const notes = [523, 659, 784, 1047];
    for (let i = 0; i < notes.length; i++) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      const t = ctx.currentTime + i * 0.1;
      g.gain.setValueAtTime(vol * 0.2, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
      g.connect(ctx.destination);
      osc.type = 'square';
      osc.frequency.setValueAtTime(notes[i], t);
      osc.connect(g);
      osc.start(t); osc.stop(t + 0.15);
    }
  }

  private synthPlace(ctx: AudioContext, vol: number): void {
    const osc = ctx.createOscillator();
    const g = this.gain(ctx, vol * 0.3, 0.06);
    osc.type = 'square';
    osc.frequency.setValueAtTime(this.vary(1200), ctx.currentTime);
    osc.connect(g);
    osc.start(); osc.stop(ctx.currentTime + 0.06);
  }

  private synthClick(ctx: AudioContext, vol: number): void {
    const osc = ctx.createOscillator();
    const g = this.gain(ctx, vol * 0.2, 0.03);
    osc.type = 'square';
    osc.frequency.setValueAtTime(this.vary(1000, 0.04), ctx.currentTime);
    osc.connect(g);
    osc.start(); osc.stop(ctx.currentTime + 0.03);
  }

  private synthClickConfirm(ctx: AudioContext, vol: number): void {
    for (let i = 0; i < 2; i++) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      const t = ctx.currentTime + i * 0.06;
      g.gain.setValueAtTime(vol * 0.2, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
      g.connect(ctx.destination);
      osc.type = 'square';
      osc.frequency.setValueAtTime(this.vary(800, 0.04) + i * 400, t);
      osc.connect(g);
      osc.start(t); osc.stop(t + 0.05);
    }
  }

  private synthClickCancel(ctx: AudioContext, vol: number): void {
    const osc = ctx.createOscillator();
    const g = this.gain(ctx, vol * 0.2, 0.08);
    osc.type = 'square';
    osc.frequency.setValueAtTime(this.vary(600, 0.04), ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(this.vary(200, 0.04), ctx.currentTime + 0.08);
    osc.connect(g);
    osc.start(); osc.stop(ctx.currentTime + 0.08);
  }

  private synthClickSelect(ctx: AudioContext, vol: number): void {
    const osc = ctx.createOscillator();
    const g = this.gain(ctx, vol * 0.15, 0.04);
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(this.vary(1200, 0.04), ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(this.vary(800, 0.04), ctx.currentTime + 0.04);
    osc.connect(g);
    osc.start(); osc.stop(ctx.currentTime + 0.04);
  }

  private synthClickCycle(ctx: AudioContext, vol: number): void {
    const osc = ctx.createOscillator();
    const g = this.gain(ctx, vol * 0.12, 0.025);
    osc.type = 'square';
    osc.frequency.setValueAtTime(this.vary(1400, 0.04), ctx.currentTime);
    osc.connect(g);
    osc.start(); osc.stop(ctx.currentTime + 0.025);
  }

  private synthError(ctx: AudioContext, vol: number): void {
    const osc = ctx.createOscillator();
    const g = this.gain(ctx, vol * 0.4, 0.15);
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(this.vary(200, 0.05), ctx.currentTime);
    osc.frequency.setValueAtTime(this.vary(100, 0.05), ctx.currentTime + 0.07);
    osc.connect(g);
    osc.start(); osc.stop(ctx.currentTime + 0.15);
  }

  // ── Enemy attack sounds ──

  private synthAtkGrunt(ctx: AudioContext, vol: number): void {
    const osc = ctx.createOscillator();
    const dur = this.vary(0.1);
    const g = this.gain(ctx, vol * 0.25, dur);
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(this.vary(180), ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(this.vary(60), ctx.currentTime + dur);
    osc.connect(g);
    osc.start(); osc.stop(ctx.currentTime + dur);
  }

  /** Runner attack: short aggressive bark/yelp — like a dog snapping */
  private synthAtkRunner(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const dur = this.vary(0.10);
    const g = this.gain(ctx, vol * 0.3, dur);

    // Primary: sharp bark — triangle with fast pitch drop
    const bark = ctx.createOscillator();
    bark.type = 'triangle';
    bark.frequency.setValueAtTime(this.vary(420), t);
    bark.frequency.exponentialRampToValueAtTime(this.vary(180), t + dur * 0.4);
    bark.frequency.exponentialRampToValueAtTime(this.vary(120), t + dur);

    // Harmonic growl layer for texture
    const growl = ctx.createOscillator();
    growl.type = 'sawtooth';
    growl.frequency.setValueAtTime(this.vary(210), t);
    growl.frequency.exponentialRampToValueAtTime(this.vary(90), t + dur);
    const growlGain = ctx.createGain();
    growlGain.gain.setValueAtTime(0.3, t);
    growlGain.gain.exponentialRampToValueAtTime(0.01, t + dur);

    bark.connect(g);
    growl.connect(growlGain).connect(g);
    bark.start(t); bark.stop(t + dur);
    growl.start(t); growl.stop(t + dur);
  }

  /** Runner dash: low rumbling growl — dog charging */
  private synthRunnerGrowl(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const dur = this.vary(0.18);
    const g = this.gain(ctx, vol * 0.15, dur);

    // Low rumble
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(this.vary(90), t);
    osc.frequency.exponentialRampToValueAtTime(this.vary(50), t + dur);

    // Noise-like texture via detuned sawtooth
    const noise = ctx.createOscillator();
    noise.type = 'sawtooth';
    noise.frequency.setValueAtTime(this.vary(95), t);
    noise.frequency.exponentialRampToValueAtTime(this.vary(45), t + dur);
    const nGain = ctx.createGain();
    nGain.gain.setValueAtTime(0.2, t);
    nGain.gain.exponentialRampToValueAtTime(0.01, t + dur);

    osc.connect(g);
    noise.connect(nGain).connect(g);
    osc.start(t); osc.stop(t + dur);
    noise.start(t); noise.stop(t + dur);
  }

  private synthAtkSwarm(ctx: AudioContext, vol: number): void {
    const baseF = this.vary(2000);
    for (let i = 0; i < 2; i++) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      g.gain.setValueAtTime(vol * 0.12, ctx.currentTime + i * 0.03);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.03 + 0.025);
      g.connect(ctx.destination);
      osc.type = 'square';
      osc.frequency.setValueAtTime(baseF + i * this.vary(400), ctx.currentTime + i * 0.03);
      osc.connect(g);
      osc.start(ctx.currentTime + i * 0.03);
      osc.stop(ctx.currentTime + i * 0.03 + 0.025);
    }
  }

  private synthAtkTank(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(this.vary(180), t);
    osc.frequency.exponentialRampToValueAtTime(this.vary(35), t + 0.3);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(this.vary(800), t);
    lp.frequency.exponentialRampToValueAtTime(100, t + 0.3);
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol * 0.6, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
    g.connect(ctx.destination);
    osc.connect(lp); lp.connect(g);
    osc.start(t); osc.stop(t + 0.3);
    const clang = ctx.createOscillator();
    clang.type = 'square';
    clang.frequency.setValueAtTime(this.vary(500), t);
    clang.frequency.exponentialRampToValueAtTime(this.vary(100), t + 0.06);
    const cg = ctx.createGain();
    cg.gain.setValueAtTime(vol * 0.4, t);
    cg.gain.exponentialRampToValueAtTime(0.001, t + 0.06);
    cg.connect(ctx.destination);
    clang.connect(cg);
    clang.start(t); clang.stop(t + 0.06);
    const nLen = Math.floor(ctx.sampleRate * 0.15);
    const nBuf = ctx.createBuffer(1, nLen, ctx.sampleRate);
    const nd = nBuf.getChannelData(0);
    const grain = Math.floor(ctx.sampleRate * 0.002);
    let s = 0;
    for (let i = 0; i < nLen; i++) {
      if (i % grain === 0) s = Math.random() * 2 - 1;
      nd[i] = s * (1 - i / nLen);
    }
    const nSrc = ctx.createBufferSource();
    nSrc.buffer = nBuf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(this.vary(600), t);
    bp.Q.setValueAtTime(1, t);
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(vol * 0.35, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    ng.connect(ctx.destination);
    nSrc.connect(bp); bp.connect(ng);
    nSrc.start(t); nSrc.stop(t + 0.15);
  }

  private synthAtkBrute(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const bufSize = ctx.sampleRate * 0.18;
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / bufSize);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filt = ctx.createBiquadFilter();
    filt.type = 'lowpass';
    filt.frequency.setValueAtTime(this.vary(500), t);
    filt.frequency.exponentialRampToValueAtTime(this.vary(60), t + 0.18);
    const g = this.gain(ctx, vol * 0.6, 0.18);
    src.connect(filt); filt.connect(g);
    src.start(t); src.stop(t + 0.18);
    const osc = ctx.createOscillator();
    const og = this.gain(ctx, vol * 0.4, 0.15);
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(this.vary(80), t);
    osc.frequency.exponentialRampToValueAtTime(this.vary(30), t + 0.15);
    osc.connect(og);
    osc.start(t); osc.stop(t + 0.15);
  }

  private synthAtkPhantom(ctx: AudioContext, vol: number): void {
    const osc = ctx.createOscillator();
    const g = this.gain(ctx, vol * 0.15, 0.2);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(this.vary(800), ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(this.vary(200), ctx.currentTime + 0.2);
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.setValueAtTime(this.vary(20), ctx.currentTime);
    lfoGain.gain.setValueAtTime(this.vary(80), ctx.currentTime);
    lfo.connect(lfoGain); lfoGain.connect(osc.frequency);
    osc.connect(g);
    lfo.start(); lfo.stop(ctx.currentTime + 0.2);
    osc.start(); osc.stop(ctx.currentTime + 0.2);
  }

  private synthAtkDrone(ctx: AudioContext, vol: number): void {
    const osc = ctx.createOscillator();
    const g = this.gain(ctx, vol * 0.2, 0.08);
    osc.type = 'square';
    osc.frequency.setValueAtTime(this.vary(440), ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(this.vary(880), ctx.currentTime + 0.04);
    osc.frequency.exponentialRampToValueAtTime(this.vary(330), ctx.currentTime + 0.08);
    osc.connect(g);
    osc.start(); osc.stop(ctx.currentTime + 0.08);
  }

  private synthAtkWasp(ctx: AudioContext, vol: number): void {
    const osc = ctx.createOscillator();
    const g = this.gain(ctx, vol * 0.18, 0.05);
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(this.vary(2500), ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(this.vary(800), ctx.currentTime + 0.05);
    osc.connect(g);
    osc.start(); osc.stop(ctx.currentTime + 0.05);
  }

  private synthAtkBomber(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(this.vary(200), t);
    osc.frequency.exponentialRampToValueAtTime(this.vary(50), t + 0.5);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(this.vary(900), t);
    lp.frequency.exponentialRampToValueAtTime(120, t + 0.5);
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol * 0.6, t);
    g.gain.setValueAtTime(vol * 0.6, t + 0.2);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
    g.connect(ctx.destination);
    osc.connect(lp); lp.connect(g);
    osc.start(t); osc.stop(t + 0.5);
    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(this.vary(80), t);
    sub.frequency.exponentialRampToValueAtTime(this.vary(25), t + 0.4);
    const sg = ctx.createGain();
    sg.gain.setValueAtTime(vol * 0.5, t);
    sg.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
    sg.connect(ctx.destination);
    sub.connect(sg);
    sub.start(t); sub.stop(t + 0.4);
    const nLen = Math.floor(ctx.sampleRate * 0.35);
    const nBuf = ctx.createBuffer(1, nLen, ctx.sampleRate);
    const nd = nBuf.getChannelData(0);
    const grain = Math.floor(ctx.sampleRate * 0.003);
    let s = 0;
    for (let i = 0; i < nLen; i++) {
      if (i % grain === 0) s = Math.random() * 2 - 1;
      nd[i] = (s * 0.5 + (Math.random() * 2 - 1) * 0.5) * (1 - i / nLen);
    }
    const nSrc = ctx.createBufferSource();
    nSrc.buffer = nBuf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(this.vary(500), t);
    bp.Q.setValueAtTime(1, t);
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(vol * 0.4, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
    ng.connect(ctx.destination);
    nSrc.connect(bp); bp.connect(ng);
    nSrc.start(t); nSrc.stop(t + 0.35);
  }

  private synthAtkSpecter(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const g = this.gain(ctx, vol * 0.4, 0.2);
    osc1.type = 'sine';
    osc2.type = 'sine';
    const f1 = this.vary(600);
    osc1.frequency.setValueAtTime(f1, t);
    osc2.frequency.setValueAtTime(f1 + this.vary(7, 0.3), t);
    osc1.frequency.exponentialRampToValueAtTime(this.vary(150), t + 0.2);
    osc2.frequency.exponentialRampToValueAtTime(this.vary(157), t + 0.2);
    osc1.connect(g); osc2.connect(g);
    osc1.start(t); osc1.stop(t + 0.2);
    osc2.start(t); osc2.stop(t + 0.2);
    const bufSize = ctx.sampleRate * 0.12;
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / bufSize) * 0.5;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.setValueAtTime(2000, t);
    const ng = this.gain(ctx, vol * 0.2, 0.12);
    src.connect(hp); hp.connect(ng);
    src.start(t); src.stop(t + 0.12);
  }

  // ── Enemy shooting SFX ──

  private synthEnemyShootHeavy(ctx: AudioContext, vol: number, freq: number): void {
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = this.gain(ctx, vol * 0.5, 0.22);
    osc.type = 'triangle';
    const vFreq = this.vary(freq);
    osc.frequency.setValueAtTime(vFreq, t);
    osc.frequency.exponentialRampToValueAtTime(vFreq * 0.25, t + 0.22);
    osc.connect(g);
    osc.start(t); osc.stop(t + 0.22);
    const bufSize = ctx.sampleRate * 0.06;
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / bufSize);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filt = ctx.createBiquadFilter();
    filt.type = 'lowpass';
    filt.frequency.setValueAtTime(freq * 2, t);
    filt.frequency.exponentialRampToValueAtTime(freq * 0.5, t + 0.06);
    const ng = this.gain(ctx, vol * 0.3, 0.06);
    src.connect(filt); filt.connect(ng);
    src.start(t); src.stop(t + 0.06);
  }

  private synthEnemyShootZap(ctx: AudioContext, vol: number, freq: number): void {
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = this.gain(ctx, vol * 0.35, 0.12);
    osc.type = 'sawtooth';
    const vf = this.vary(freq);
    osc.frequency.setValueAtTime(vf, t);
    osc.frequency.exponentialRampToValueAtTime(vf * 0.3, t + 0.12);
    osc.connect(g);
    osc.start(t); osc.stop(t + 0.12);
  }

  private synthEnemyShootBomber(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(this.vary(150), t);
    osc.frequency.exponentialRampToValueAtTime(this.vary(30), t + 0.35);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(this.vary(700), t);
    lp.frequency.exponentialRampToValueAtTime(80, t + 0.35);
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol * 0.5, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
    g.connect(ctx.destination);
    osc.connect(lp); lp.connect(g);
    osc.start(t); osc.stop(t + 0.35);
    const nLen = Math.floor(ctx.sampleRate * 0.2);
    const nBuf = ctx.createBuffer(1, nLen, ctx.sampleRate);
    const nd = nBuf.getChannelData(0);
    for (let i = 0; i < nLen; i++) nd[i] = (Math.random() * 2 - 1) * (1 - i / nLen);
    const nSrc = ctx.createBufferSource();
    nSrc.buffer = nBuf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(this.vary(350), t);
    bp.Q.setValueAtTime(1.5, t);
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(vol * 0.35, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
    ng.connect(ctx.destination);
    nSrc.connect(bp); bp.connect(ng);
    nSrc.start(t); nSrc.stop(t + 0.2);
  }

  // ── Reward/Juice SFX ──

  private synthStreak(ctx: AudioContext, vol: number): void {
    const notes = [880, 1100, 1320];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const g = this.gain(ctx, vol * 0.2, 0.08);
      osc.type = 'square';
      osc.frequency.setValueAtTime(this.vary(freq, 0.04), ctx.currentTime + i * 0.06);
      osc.connect(g);
      osc.start(ctx.currentTime + i * 0.06);
      osc.stop(ctx.currentTime + i * 0.06 + 0.06);
    });
  }

  private synthComponentBreak(ctx: AudioContext, vol: number): void {
    const bufLen = ctx.sampleRate * 0.08;
    const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / bufLen);
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const ng = this.gain(ctx, vol * 0.3, 0.08);
    noise.connect(ng);
    noise.start();
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(this.vary(900), ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(this.vary(200), ctx.currentTime + 0.15);
    const g = this.gain(ctx, vol * 0.2, 0.15);
    osc.connect(g);
    osc.start();
    osc.stop(ctx.currentTime + 0.15);
  }

  private synthRepair(ctx: AudioContext, vol: number): void {
    const notes = [660, 880, 1100];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const g = this.gain(ctx, vol * 0.15, 0.15);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(this.vary(freq, 0.03), ctx.currentTime + i * 0.07);
      osc.connect(g);
      osc.start(ctx.currentTime + i * 0.07);
      osc.stop(ctx.currentTime + i * 0.07 + 0.12);
    });
  }

  private synthCoin(ctx: AudioContext, vol: number): void {
    const osc = ctx.createOscillator();
    const g = this.gain(ctx, vol * 0.12, 0.1);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(this.vary(1400), ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(this.vary(2200), ctx.currentTime + 0.05);
    osc.connect(g);
    osc.start();
    osc.stop(ctx.currentTime + 0.1);
  }

  private synthWaveClear(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const melody: { freq: number; start: number; dur: number }[] = [
      { freq: 523, start: 0,    dur: 0.12 },
      { freq: 659, start: 0.13, dur: 0.12 },
      { freq: 784, start: 0.26, dur: 0.12 },
      { freq: 1047,start: 0.42, dur: 0.35 },
    ];
    for (const note of melody) {
      const osc = ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.setValueAtTime(this.vary(note.freq, 0.02), t + note.start);
      const g = ctx.createGain();
      g.gain.setValueAtTime(vol * 0.22, t + note.start);
      g.gain.setValueAtTime(vol * 0.22, t + note.start + note.dur * 0.7);
      g.gain.exponentialRampToValueAtTime(0.001, t + note.start + note.dur);
      g.connect(ctx.destination);
      osc.connect(g);
      osc.start(t + note.start);
      osc.stop(t + note.start + note.dur);
    }
    const harm = ctx.createOscillator();
    harm.type = 'triangle';
    harm.frequency.setValueAtTime(this.vary(659, 0.02), t + 0.42);
    const hg = ctx.createGain();
    hg.gain.setValueAtTime(vol * 0.1, t + 0.42);
    hg.gain.exponentialRampToValueAtTime(0.001, t + 0.8);
    hg.connect(ctx.destination);
    harm.connect(hg);
    harm.start(t + 0.42); harm.stop(t + 0.8);
  }

  private synthEvolve(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const notes = [523, 659, 784, 1047, 1319];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const g = this.gain(ctx, vol * 0.18, 0.3);
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, t + i * 0.06);
      osc.connect(g);
      osc.start(t + i * 0.06);
      osc.stop(t + i * 0.06 + 0.2);
    });
    const shimmer = ctx.createOscillator();
    const sg = this.gain(ctx, vol * 0.1, 0.4);
    shimmer.type = 'sine';
    shimmer.frequency.setValueAtTime(2637, t + 0.24);
    shimmer.frequency.exponentialRampToValueAtTime(3520, t + 0.55);
    shimmer.connect(sg);
    shimmer.start(t + 0.24);
    shimmer.stop(t + 0.6);
  }

  private synthMineLay(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const thud = ctx.createOscillator();
    const thudGain = this.gain(ctx, vol * 0.25, 0.15);
    thud.type = 'sine';
    thud.frequency.setValueAtTime(200, t);
    thud.frequency.exponentialRampToValueAtTime(80, t + 0.12);
    thud.connect(thudGain);
    thud.start(t);
    thud.stop(t + 0.15);
    const ping = ctx.createOscillator();
    const pingGain = this.gain(ctx, vol * 0.12, 0.06);
    ping.type = 'square';
    ping.frequency.setValueAtTime(1200, t + 0.03);
    ping.frequency.exponentialRampToValueAtTime(600, t + 0.08);
    ping.connect(pingGain);
    ping.start(t + 0.03);
    ping.stop(t + 0.09);
  }

  // ── New game event synths ──

  private synthVictory(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const notes = [523, 659, 784, 1047];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      g.gain.setValueAtTime(vol * 0.25, t + i * 0.12);
      g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.12 + 0.4);
      g.connect(ctx.destination);
      osc.type = 'square';
      osc.frequency.setValueAtTime(freq, t + i * 0.12);
      osc.connect(g);
      osc.start(t + i * 0.12);
      osc.stop(t + i * 0.12 + 0.4);
    });
    const chord = ctx.createOscillator();
    const cg = ctx.createGain();
    cg.gain.setValueAtTime(vol * 0.15, t + 0.5);
    cg.gain.exponentialRampToValueAtTime(0.001, t + 1.2);
    cg.connect(ctx.destination);
    chord.type = 'triangle';
    chord.frequency.setValueAtTime(1047, t + 0.5);
    chord.connect(cg);
    chord.start(t + 0.5);
    chord.stop(t + 1.2);
  }

  private synthDefeat(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const melody: { freq: number; start: number; dur: number }[] = [
      { freq: 440, start: 0,    dur: 0.25 },
      { freq: 415, start: 0.28, dur: 0.25 },
      { freq: 349, start: 0.56, dur: 0.25 },
      { freq: 330, start: 0.84, dur: 0.55 },
    ];
    for (const note of melody) {
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(this.vary(note.freq, 0.02), t + note.start);
      osc.frequency.exponentialRampToValueAtTime(note.freq * 0.92, t + note.start + note.dur);
      const g = ctx.createGain();
      g.gain.setValueAtTime(vol * 0.22, t + note.start);
      g.gain.setValueAtTime(vol * 0.18, t + note.start + note.dur * 0.6);
      g.gain.exponentialRampToValueAtTime(0.001, t + note.start + note.dur);
      g.connect(ctx.destination);
      osc.connect(g);
      osc.start(t + note.start);
      osc.stop(t + note.start + note.dur);
    }
    const drone = ctx.createOscillator();
    drone.type = 'sine';
    drone.frequency.setValueAtTime(this.vary(165), t);
    const dg = ctx.createGain();
    dg.gain.setValueAtTime(vol * 0.12, t);
    dg.gain.exponentialRampToValueAtTime(0.001, t + 1.4);
    dg.connect(ctx.destination);
    drone.connect(dg);
    drone.start(t); drone.stop(t + 1.4);
  }

  private synthBaseDestroyed(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    this.synthExplosion(ctx, vol, 1.8);
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol * 0.2, t);
    g.gain.setValueAtTime(vol * 0.2, t + 0.3);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.8);
    g.connect(ctx.destination);
    osc.type = 'square';
    osc.frequency.setValueAtTime(880, t);
    osc.frequency.setValueAtTime(660, t + 0.1);
    osc.frequency.setValueAtTime(880, t + 0.2);
    osc.frequency.setValueAtTime(660, t + 0.3);
    osc.frequency.exponentialRampToValueAtTime(110, t + 0.8);
    osc.connect(g);
    osc.start(t);
    osc.stop(t + 0.8);
  }

  private synthEnemySpawn(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol * 0.1, t);
    g.gain.linearRampToValueAtTime(vol * 0.15, t + 0.03);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    g.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(this.vary(220), t);
    osc.frequency.exponentialRampToValueAtTime(this.vary(110), t + 0.15);
    osc.connect(g);
    osc.start(t);
    osc.stop(t + 0.15);
  }

  private synthRobotDeath(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const bufLen = ctx.sampleRate * 0.15;
    const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) data[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(this.vary(800), t);
    bp.Q.setValueAtTime(this.vary(2, 0.15), t);
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(vol * 0.3, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    ng.connect(ctx.destination);
    noise.connect(bp);
    bp.connect(ng);
    noise.start(t);
    noise.stop(t + 0.15);
    const hissBuf = ctx.createBuffer(1, ctx.sampleRate * 0.4, ctx.sampleRate);
    const hissData = hissBuf.getChannelData(0);
    for (let i = 0; i < hissData.length; i++) hissData[i] = Math.random() * 2 - 1;
    const hiss = ctx.createBufferSource();
    hiss.buffer = hissBuf;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.setValueAtTime(4000, t + 0.1);
    const hg = ctx.createGain();
    hg.gain.setValueAtTime(0.001, t);
    hg.gain.linearRampToValueAtTime(vol * 0.12, t + 0.12);
    hg.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
    hg.connect(ctx.destination);
    hiss.connect(hp);
    hp.connect(hg);
    hiss.start(t + 0.08);
    hiss.stop(t + 0.5);
  }

  private synthPhaseChange(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const notes = [523, 659, 784];
    const noteDur = 0.09;
    const gap = 0.11;
    for (let i = 0; i < notes.length; i++) {
      const start = t + i * gap;
      const osc = ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.setValueAtTime(notes[i], start);
      const g = ctx.createGain();
      g.gain.setValueAtTime(vol * 0.25, start);
      g.gain.setValueAtTime(vol * 0.25, start + noteDur * 0.7);
      g.gain.exponentialRampToValueAtTime(0.001, start + noteDur);
      g.connect(ctx.destination);
      osc.connect(g);
      osc.start(start); osc.stop(start + noteDur);
    }
    const finalStart = t + notes.length * gap;
    const osc2 = ctx.createOscillator();
    osc2.type = 'square';
    osc2.frequency.setValueAtTime(1047, finalStart);
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(vol * 0.3, finalStart);
    g2.gain.exponentialRampToValueAtTime(0.001, finalStart + 0.25);
    g2.connect(ctx.destination);
    osc2.connect(g2);
    osc2.start(finalStart); osc2.stop(finalStart + 0.25);
  }

  private synthLevelUnlock(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const clankBuf = ctx.createBuffer(1, Math.round(ctx.sampleRate * 0.15), ctx.sampleRate);
    const cd = clankBuf.getChannelData(0);
    for (let i = 0; i < cd.length; i++) cd[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.02));
    const clankSrc = ctx.createBufferSource();
    clankSrc.buffer = clankBuf;
    const clankG = ctx.createGain();
    clankG.gain.setValueAtTime(vol * 0.4, t);
    clankG.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    const clankF = ctx.createBiquadFilter();
    clankF.type = 'bandpass'; clankF.frequency.value = 2200; clankF.Q.value = 3;
    clankSrc.connect(clankF).connect(clankG).connect(ctx.destination);
    clankSrc.start(t); clankSrc.stop(t + 0.15);
    const snap = ctx.createOscillator();
    const snapG = ctx.createGain();
    snap.type = 'sawtooth';
    snap.frequency.setValueAtTime(800, t + 0.08);
    snap.frequency.exponentialRampToValueAtTime(120, t + 0.25);
    snapG.gain.setValueAtTime(vol * 0.2, t + 0.08);
    snapG.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
    snap.connect(snapG).connect(ctx.destination);
    snap.start(t + 0.08); snap.stop(t + 0.25);
    [523, 659, 784, 1047].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      const s = t + 0.2 + i * 0.07;
      g.gain.setValueAtTime(vol * 0.15, s);
      g.gain.exponentialRampToValueAtTime(0.001, s + 0.35);
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, s);
      osc.connect(g).connect(ctx.destination);
      osc.start(s); osc.stop(s + 0.35);
    });
  }

  private synthComponentUnlock(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const freqs = [1200, 1600, 2000, 2400];
    freqs.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      g.gain.setValueAtTime(vol * 0.12, t + i * 0.05);
      g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.05 + 0.15);
      g.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(f, t + i * 0.05);
      osc.connect(g);
      osc.start(t + i * 0.05);
      osc.stop(t + i * 0.05 + 0.15);
    });
  }

  private synthGridExpand(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    for (let i = 0; i < 4; i++) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      g.gain.setValueAtTime(vol * 0.15, t + i * 0.06);
      g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.06 + 0.04);
      g.connect(ctx.destination);
      osc.type = 'square';
      osc.frequency.setValueAtTime(this.vary(200) + i * this.vary(60), t + i * 0.06);
      osc.connect(g);
      osc.start(t + i * 0.06);
      osc.stop(t + i * 0.06 + 0.04);
    }
  }

  private synthPlatinumReveal(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const freqs = [1047, 1319, 1568, 2093];
    freqs.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t + i * 0.06);
      g.gain.linearRampToValueAtTime(vol * 0.15, t + i * 0.06 + 0.05);
      g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.06 + 0.6);
      g.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(f, t + i * 0.06);
      osc.frequency.linearRampToValueAtTime(f * 1.02, t + i * 0.06 + 0.3);
      osc.connect(g);
      osc.start(t + i * 0.06);
      osc.stop(t + i * 0.06 + 0.6);
    });
    const bass = ctx.createOscillator();
    const bg = ctx.createGain();
    bg.gain.setValueAtTime(vol * 0.1, t);
    bg.gain.exponentialRampToValueAtTime(0.001, t + 0.8);
    bg.connect(ctx.destination);
    bass.type = 'triangle';
    bass.frequency.setValueAtTime(523, t);
    bass.connect(bg);
    bass.start(t);
    bass.stop(t + 0.8);
  }

  private synthSteamVent(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const dur = 0.8;
    const buf = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(6000, t);
    bp.frequency.exponentialRampToValueAtTime(1500, t + dur);
    bp.Q.setValueAtTime(1.5, t);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.001, t);
    g.gain.linearRampToValueAtTime(vol * 0.25, t + 0.03);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    noise.connect(bp);
    bp.connect(g);
    g.connect(ctx.destination);
    noise.start(t);
    noise.stop(t + dur);
  }

  private synthJumpBoing(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(this.vary(180), t);
    osc.frequency.exponentialRampToValueAtTime(this.vary(800), t + 0.06);
    osc.frequency.exponentialRampToValueAtTime(this.vary(350), t + 0.2);
    const osc2 = ctx.createOscillator();
    osc2.type = 'triangle';
    osc2.frequency.setValueAtTime(this.vary(600), t);
    osc2.frequency.exponentialRampToValueAtTime(this.vary(1200), t + 0.04);
    osc2.frequency.exponentialRampToValueAtTime(this.vary(400), t + 0.15);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.001, t);
    g.gain.linearRampToValueAtTime(vol * 0.2, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(0.001, t);
    g2.gain.linearRampToValueAtTime(vol * 0.08, t + 0.01);
    g2.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
    osc.connect(g); g.connect(ctx.destination);
    osc2.connect(g2); g2.connect(ctx.destination);
    osc.start(t); osc.stop(t + 0.3);
    osc2.start(t); osc2.stop(t + 0.25);
  }

  private synthJumpCompress(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const dur = 0.2;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(this.vary(250), t);
    osc.frequency.exponentialRampToValueAtTime(this.vary(120), t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.001, t);
    g.gain.linearRampToValueAtTime(vol * 0.06, t + dur * 0.7);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.connect(g); g.connect(ctx.destination);
    osc.start(t); osc.stop(t + dur + 0.05);
  }

  private synthCombo(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const notes = [880, 1320];
    for (let i = 0; i < notes.length; i++) {
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(notes[i], t + i * 0.08);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t + i * 0.08);
      g.gain.linearRampToValueAtTime(vol * 0.15, t + i * 0.08 + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.08 + 0.25);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(t + i * 0.08);
      osc.stop(t + i * 0.08 + 0.3);
    }
  }

  // ── Ambient life SFX ──

  private synthRadarPing(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol * 0.12, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    g.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(this.vary(1200), t);
    osc.frequency.exponentialRampToValueAtTime(this.vary(800), t + 0.12);
    osc.connect(g);
    osc.start(t);
    osc.stop(t + 0.15);
    const osc2 = ctx.createOscillator();
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(vol * 0.05, t);
    g2.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
    g2.connect(ctx.destination);
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(this.vary(2400), t);
    osc2.connect(g2);
    osc2.start(t);
    osc2.stop(t + 0.25);
  }

  private synthServoTurn(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const dur = this.vary(0.08);
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol * 0.06, t);
    g.gain.linearRampToValueAtTime(vol * 0.1, t + dur * 0.3);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    g.connect(ctx.destination);
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(this.vary(180), t);
    osc.frequency.linearRampToValueAtTime(this.vary(400), t + dur * 0.5);
    osc.frequency.linearRampToValueAtTime(this.vary(250), t + dur);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(1200, t);
    osc.connect(lp);
    lp.connect(g);
    osc.start(t);
    osc.stop(t + dur);
  }

  private synthEnemyGrowl(ctx: AudioContext, vol: number, basePitch = 90): void {
    const t = ctx.currentTime;
    const dur = this.vary(0.25);
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol * 0.08, t);
    g.gain.linearRampToValueAtTime(vol * 0.12, t + dur * 0.2);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    g.connect(ctx.destination);
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(this.vary(basePitch), t);
    osc.frequency.linearRampToValueAtTime(this.vary(basePitch * 0.7), t + dur);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(this.vary(basePitch * 2.5), t);
    bp.Q.setValueAtTime(3, t);
    osc.connect(bp);
    bp.connect(g);
    osc.start(t);
    osc.stop(t + dur);
    const noiseDur = dur * 0.6;
    const buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * noiseDur), ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1);
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(vol * 0.04, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + noiseDur);
    ng.connect(ctx.destination);
    const nbp = ctx.createBiquadFilter();
    nbp.type = 'lowpass';
    nbp.frequency.setValueAtTime(this.vary(300), t);
    noise.connect(nbp);
    nbp.connect(ng);
    noise.start(t);
    noise.stop(t + noiseDur);
  }

  private synthEnemyDeathGrunt(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const dur = this.vary(0.18);
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol * 0.1, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    g.connect(ctx.destination);
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(this.vary(200), t);
    osc.frequency.exponentialRampToValueAtTime(this.vary(50), t + dur);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(250, t);
    bp.Q.setValueAtTime(2, t);
    osc.connect(bp);
    bp.connect(g);
    osc.start(t);
    osc.stop(t + dur);
    const nbuf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * 0.05), ctx.sampleRate);
    const nd = nbuf.getChannelData(0);
    for (let i = 0; i < nd.length; i++) nd[i] = (Math.random() * 2 - 1);
    const ns = ctx.createBufferSource();
    ns.buffer = nbuf;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(vol * 0.06, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
    ng.connect(ctx.destination);
    ns.connect(ng);
    ns.start(t);
    ns.stop(t + 0.05);
  }

  private synthRobotClank(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol * 0.1, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
    g.connect(ctx.destination);
    osc.type = 'square';
    osc.frequency.setValueAtTime(this.vary(2000), t);
    osc.frequency.exponentialRampToValueAtTime(this.vary(600), t + 0.04);
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.setValueAtTime(800, t);
    osc.connect(hp);
    hp.connect(g);
    osc.start(t);
    osc.stop(t + 0.12);
    const osc2 = ctx.createOscillator();
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(vol * 0.06, t + 0.01);
    g2.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    g2.connect(ctx.destination);
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(this.vary(3200), t);
    osc2.connect(g2);
    osc2.start(t);
    osc2.stop(t + 0.15);
  }

  private synthBurnSizzle(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const dur = 0.25;
    const bufLen = Math.ceil(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) data[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(this.vary(4000), t);
    bp.Q.setValueAtTime(2, t);
    const bp2 = ctx.createBiquadFilter();
    bp2.type = 'bandpass';
    bp2.frequency.setValueAtTime(this.vary(1500), t);
    bp2.Q.setValueAtTime(3, t);
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol * 0.12, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    g.connect(ctx.destination);
    noise.connect(bp);
    bp.connect(g);
    const noise2 = ctx.createBufferSource();
    noise2.buffer = buf;
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(vol * 0.06, t);
    g2.gain.exponentialRampToValueAtTime(0.001, t + dur * 0.7);
    g2.connect(ctx.destination);
    noise2.connect(bp2);
    bp2.connect(g2);
    noise.start(t);
    noise.stop(t + dur);
    noise2.start(t);
    noise2.stop(t + dur * 0.7);
  }

  private synthMedalReveal(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const freqs = [1480, 1865];
    freqs.forEach((f) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(f, t);
      g.gain.setValueAtTime(vol * 0.2, t);
      g.gain.exponentialRampToValueAtTime(vol * 0.08, t + 0.15);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.8);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.8);
    });
    const shimmerFreqs = [2350, 2800, 3520];
    shimmerFreqs.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(f, t + i * 0.05);
      osc.frequency.linearRampToValueAtTime(f * 1.015, t + i * 0.05 + 0.4);
      g.gain.setValueAtTime(0.001, t + i * 0.05);
      g.gain.linearRampToValueAtTime(vol * 0.08, t + i * 0.05 + 0.03);
      g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.05 + 0.5);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(t + i * 0.05);
      osc.stop(t + i * 0.05 + 0.5);
    });
    const bass = ctx.createOscillator();
    const bg = ctx.createGain();
    bass.type = 'triangle';
    bass.frequency.setValueAtTime(740, t);
    bg.gain.setValueAtTime(vol * 0.12, t);
    bg.gain.exponentialRampToValueAtTime(0.001, t + 0.6);
    bass.connect(bg);
    bg.connect(ctx.destination);
    bass.start(t);
    bass.stop(t + 0.6);
    const noiseDur = 0.015;
    const buf = ctx.createBuffer(1, ctx.sampleRate * noiseDur, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1);
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 4000;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(vol * 0.15, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.03);
    noise.connect(hp);
    hp.connect(ng);
    ng.connect(ctx.destination);
    noise.start(t);
    noise.stop(t + noiseDur);
  }

  private synthStamp(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g1 = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(180, t);
    osc.frequency.exponentialRampToValueAtTime(60, t + 0.08);
    g1.gain.setValueAtTime(vol * 0.35, t);
    g1.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
    osc.connect(g1);
    g1.connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.1);
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.04, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1);
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 2000;
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(vol * 0.18, t);
    g2.gain.exponentialRampToValueAtTime(0.001, t + 0.04);
    noise.connect(hp);
    hp.connect(g2);
    g2.connect(ctx.destination);
    noise.start(t);
    noise.stop(t + 0.04);
  }

  // ── Terrain death SFX ──

  private synthTerrainDrown(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const sr = ctx.sampleRate;
    const pops = [
      { delay: 0.0,  freq: 350, dur: 0.15 },
      { delay: 0.25, freq: 280, dur: 0.18 },
      { delay: 0.55, freq: 200, dur: 0.22 },
    ];
    for (const p of pops) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(this.vary(p.freq), t + p.delay);
      osc.frequency.exponentialRampToValueAtTime(this.vary(p.freq * 0.4), t + p.delay + p.dur);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t + p.delay);
      g.gain.linearRampToValueAtTime(vol * 0.18, t + p.delay + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + p.delay + p.dur);
      g.connect(ctx.destination);
      osc.connect(g);
      osc.start(t + p.delay);
      osc.stop(t + p.delay + p.dur + 0.02);
    }
    const splashDur = 0.4;
    const bufLen = Math.ceil(sr * splashDur);
    const buf = ctx.createBuffer(1, bufLen, sr);
    const d = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / bufLen);
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(this.vary(1200), t);
    lp.frequency.exponentialRampToValueAtTime(300, t + splashDur);
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(vol * 0.1, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + splashDur);
    ng.connect(ctx.destination);
    noise.connect(lp);
    lp.connect(ng);
    noise.start(t);
    noise.stop(t + splashDur);
  }

  private synthTerrainBurn(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const sr = ctx.sampleRate;
    const hissDur = 1.5;
    const bufLen = Math.ceil(sr * hissDur);
    const buf = ctx.createBuffer(1, bufLen, sr);
    const d = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) d[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(this.vary(5000), t);
    bp.frequency.exponentialRampToValueAtTime(this.vary(2000), t + hissDur);
    bp.Q.setValueAtTime(1.5, t);
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(vol * 0.15, t);
    ng.gain.setValueAtTime(vol * 0.15, t + hissDur * 0.3);
    ng.gain.exponentialRampToValueAtTime(0.001, t + hissDur);
    ng.connect(ctx.destination);
    noise.connect(bp);
    bp.connect(ng);
    noise.start(t);
    noise.stop(t + hissDur);
    const screamDelay = 0.3;
    const screamDur = 1.0;
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(this.vary(600), t + screamDelay);
    osc.frequency.exponentialRampToValueAtTime(this.vary(150), t + screamDelay + screamDur);
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 6;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 30;
    lfo.connect(lfoGain);
    lfoGain.connect(osc.frequency);
    lfo.start(t + screamDelay);
    lfo.stop(t + screamDelay + screamDur);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(this.vary(2000), t + screamDelay);
    lp.frequency.exponentialRampToValueAtTime(500, t + screamDelay + screamDur);
    const og = ctx.createGain();
    og.gain.setValueAtTime(0.001, t + screamDelay);
    og.gain.linearRampToValueAtTime(vol * 0.1, t + screamDelay + 0.05);
    og.gain.exponentialRampToValueAtTime(0.001, t + screamDelay + screamDur);
    og.connect(ctx.destination);
    osc.connect(lp);
    lp.connect(og);
    osc.start(t + screamDelay);
    osc.stop(t + screamDelay + screamDur + 0.02);
  }

  private synthTerrainFall(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const sr = ctx.sampleRate;
    const fallDur = 0.7;
    for (const detune of [0, 7]) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(this.vary(500) + detune, t);
      osc.frequency.exponentialRampToValueAtTime(this.vary(80), t + fallDur);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(vol * 0.15, t + 0.03);
      g.gain.setValueAtTime(vol * 0.12, t + fallDur * 0.6);
      g.gain.exponentialRampToValueAtTime(0.001, t + fallDur);
      g.connect(ctx.destination);
      osc.connect(g);
      osc.start(t);
      osc.stop(t + fallDur + 0.02);
    }
    const impactT = t + fallDur - 0.05;
    const thud = ctx.createOscillator();
    thud.type = 'sine';
    thud.frequency.setValueAtTime(this.vary(60), impactT);
    thud.frequency.exponentialRampToValueAtTime(25, impactT + 0.2);
    const tg = ctx.createGain();
    tg.gain.setValueAtTime(0.001, impactT);
    tg.gain.linearRampToValueAtTime(vol * 0.25, impactT + 0.01);
    tg.gain.exponentialRampToValueAtTime(0.001, impactT + 0.25);
    tg.connect(ctx.destination);
    thud.connect(tg);
    thud.start(impactT);
    thud.stop(impactT + 0.3);
    const nLen = Math.ceil(sr * 0.15);
    const buf = ctx.createBuffer(1, nLen, sr);
    const d = buf.getChannelData(0);
    for (let i = 0; i < nLen; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / nLen);
    const ns = ctx.createBufferSource();
    ns.buffer = buf;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(this.vary(800), impactT);
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(vol * 0.12, impactT);
    ng.gain.exponentialRampToValueAtTime(0.001, impactT + 0.15);
    ng.connect(ctx.destination);
    ns.connect(lp);
    lp.connect(ng);
    ns.start(impactT);
    ns.stop(impactT + 0.15);
  }

  /** Mining tick — short metallic scrape (pickaxe hitting rock) */
  private synthMining(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    // Noise burst (scrape)
    const ns = ctx.createBufferSource();
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.06, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1);
    ns.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = this.vary(2500, 0.15);
    bp.Q.value = 3;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(vol * 0.08, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.06);
    ng.connect(ctx.destination);
    ns.connect(bp);
    bp.connect(ng);
    ns.start(t);
    ns.stop(t + 0.06);
    // Metallic ping
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(this.vary(800, 0.1), t);
    osc.frequency.exponentialRampToValueAtTime(this.vary(400, 0.1), t + 0.04);
    const g = this.gain(ctx, vol * 0.05, 0.04);
    osc.connect(g);
    osc.start(t);
    osc.stop(t + 0.04);
  }

  /** Harvest complete — cheerful ascending chime (resource collected) */
  private synthHarvestComplete(ctx: AudioContext, vol: number): void {
    const t = ctx.currentTime;
    const notes = [880, 1100, 1320];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(this.vary(freq, 0.02), t + i * 0.06);
      const g = ctx.createGain();
      g.gain.setValueAtTime(vol * 0.12, t + i * 0.06);
      g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.06 + 0.1);
      g.connect(ctx.destination);
      osc.connect(g);
      osc.start(t + i * 0.06);
      osc.stop(t + i * 0.06 + 0.1);
    });
  }
}
